import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MapPin, Camera, ArrowRight, CheckCircle, Compass, ChevronLeft, ChevronRight, Navigation, Clock, Search, User } from 'lucide-react';
import { AuthModal } from './Auth/AuthModal';
import { useAuth } from '../context/AuthContext';
import { getAllLocations } from '../utils/api';
import { supabase } from '../utils/supabase';
import { Location, UserLocation } from '../types';
import { getCachedRoute } from '../utils/routes';

interface LandingStats {
  totalLocations: number;
  totalUsers: number;
  totalPhotos: number;
}

// Функции для правильных склонений
const getLocationWord = (count: number): string => {
  const lastDigit = count % 10;
  const lastTwoDigits = count % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 14) {
    return 'лакацый';
  }
  
  if (lastDigit === 1) {
    return 'лакацыя';
  } else if (lastDigit >= 2 && lastDigit <= 4) {
    return 'лакацыі';
  } else {
    return 'лакацый';
  }
};

const getUserWord = (count: number): string => {
  const lastDigit = count % 10;
  const lastTwoDigits = count % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 14) {
    return 'карыстальнікаў';
  }
  
  if (lastDigit === 1) {
    return 'карыстальнік';
  } else if (lastDigit >= 2 && lastDigit <= 4) {
    return 'карыстальнікі';
  } else {
    return 'карыстальнікаў';
  }
};

const getPhotoWord = (count: number): string => {
  const lastDigit = count % 10;
  const lastTwoDigits = count % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 14) {
    return 'фотаздымкаў';
  }
  
  if (lastDigit === 1) {
    return 'фотаздымак';
  } else if (lastDigit >= 2 && lastDigit <= 4) {
    return 'фотаздымкі';
  } else {
    return 'фотаздымкаў';
  }
};

export const LandingPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('login');
  const [currentLocationIndex, setCurrentLocationIndex] = useState(0);
  const [featuredLocations, setFeaturedLocations] = useState<Location[]>([]);
  const [userLocation, setUserLocation] = useState<UserLocation | null>(null);
  const [locationDistances, setLocationDistances] = useState<Record<string, { distance: number; duration: number }>>({});
  const [stats, setStats] = useState<LandingStats>({
    totalLocations: 0,
    totalUsers: 0,
    totalPhotos: 0
  });

  // Запрос геолокации пользователя
  useEffect(() => {
    const detectLocation = async () => {
      if (navigator.geolocation) {
        const geolocationOptions = {
          enableHighAccuracy: false,
          timeout: 10000,
          maximumAge: 300000,
        };

        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const { latitude, longitude } = position.coords;
            try {
              const response = await fetch(
                `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=be`,
              );
              const data = await response.json();
              setUserLocation({
                lat: latitude,
                lng: longitude,
                city: data.city || 'Невядомы горад',
                source: 'gps'
              });
            } catch (error) {
              console.error('Памылка атрымання дадзеных пра месцазнаходжанне:', error);
              setUserLocation({
                lat: 53.9,
                lng: 27.5667,
                city: 'Мінск',
                source: 'gps'
              });
            }
          },
          async () => {
            try {
              const res = await fetch('https://ipapi.co/json/');
              const ipData = await res.json();
              setUserLocation({
                lat: ipData.latitude || 53.9,
                lng: ipData.longitude || 27.5667,
                city: ipData.city || 'Мінск',
                source: 'ip'
              });
            } catch (_) {
              setUserLocation({
                lat: 53.9,
                lng: 27.5667,
                city: 'Мінск',
                source: 'ip'
              });
            }
          },
          geolocationOptions,
        );
      } else {
        try {
          const res = await fetch('https://ipapi.co/json/');
          const ipData = await res.json();
          setUserLocation({
            lat: ipData.latitude || 53.9,
            lng: ipData.longitude || 27.5667,
            city: ipData.city || 'Мінск',
            source: 'ip'
          });
        } catch (_) {
          setUserLocation({
            lat: 53.9,
            lng: 27.5667,
            city: 'Мінск',
            source: 'ip'
          });
        }
      }
    };

    detectLocation();
  }, []);

  // Расчет расстояний до локаций
  useEffect(() => {
    if (userLocation && featuredLocations.length > 0) {
      const calculateDistances = async () => {
        const distances: Record<string, { distance: number; duration: number }> = {};
        
        for (const location of featuredLocations) {
          try {
            const routeInfo = await getCachedRoute(userLocation, location.coordinates);
            distances[location.id] = routeInfo;
          } catch (error) {
            console.error('Error calculating route:', error);
          }
        }
        
        setLocationDistances(distances);
      };

      calculateDistances();
    }
  }, [userLocation, featuredLocations]);

  // Load real data from database
  useEffect(() => {
    const loadData = async () => {
      try {
        console.log('Loading locations data...');
        // Load locations
        const locations = await getAllLocations();
        console.log('Loaded locations:', locations.length);
        
        const approvedLocations = locations.filter(loc => loc.status === 'approved');
        console.log('Approved locations:', approvedLocations.length);
        
        // Shuffle and take first 5 for carousel
        const shuffled = [...approvedLocations].sort(() => 0.5 - Math.random());
        setFeaturedLocations(shuffled.slice(0, 5));

        // Count total photos
        const totalPhotos = approvedLocations.reduce((sum, loc) => sum + (loc.images?.length || 0), 0);

        // Get user count
        const { count: userCount } = await supabase
          .from('profiles')
          .select('*', { count: 'exact', head: true });

        setStats({
          totalLocations: approvedLocations.length,
          totalUsers: userCount || 0,
          totalPhotos
        });
      } catch (error) {
        console.error('Error loading landing page data:', error);
        // Fallback data
        setStats({
          totalLocations: 180,
          totalUsers: 500,
          totalPhotos: 1200
        });
      }
    };

    loadData();
  }, []);

  // Auto-rotate featured locations (slower)
  useEffect(() => {
    if (featuredLocations.length > 0) {
      const interval = setInterval(() => {
        setCurrentLocationIndex((prev) => (prev + 1) % featuredLocations.length);
      }, 8000); // Увеличено с 5 до 8 секунд
      return () => clearInterval(interval);
    }
  }, [featuredLocations.length]);

  const nextLocation = () => {
    setCurrentLocationIndex((prev) => (prev + 1) % featuredLocations.length);
  };

  const prevLocation = () => {
    setCurrentLocationIndex((prev) => (prev - 1 + featuredLocations.length) % featuredLocations.length);
  };

  const formatDuration = (minutes: number) => {
    if (minutes > 60) {
      const hours = minutes / 60;
      const roundedHours = Math.round(hours * 10) / 10;
      return `${Number.isInteger(roundedHours) ? Math.floor(roundedHours) : roundedHours} г.`;
    }
    return `${minutes} хв`;
  };

  // Функция для обработки клика по локации в слайдере
  const handleLocationClick = (location: Location) => {
    // Переходим на главную страницу приложения с выбранной локацией
    navigate('/app', { state: { selectedLocation: location } });
  };

  const noiseDataUrl =
    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='256' height='256'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.78' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E";

  return (
    <div className="relative isolate min-h-screen">
      <div className="pointer-events-none fixed inset-0 -z-10" aria-hidden>
        <div className="absolute inset-0 bg-gradient-to-br from-[#f5faf7] via-[#fafbfc] to-[#eef3fb]" />
        <div
          className="absolute inset-0 bg-[radial-gradient(ellipse_110%_65%_at_50%_-5%,rgba(34,197,94,0.16),transparent_52%),radial-gradient(ellipse_75%_55%_at_95%_85%,rgba(59,130,246,0.11),transparent_48%),radial-gradient(ellipse_55%_45%_at_5%_95%,rgba(34,197,94,0.07),transparent_42%)]"
          aria-hidden
        />
        <div
          className="absolute inset-0 opacity-[0.2] mix-blend-soft-light"
          style={{ backgroundImage: `url("${noiseDataUrl}")` }}
        />
      </div>
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <img 
                src="https://i.ibb.co/xKYr1D9P/Group-1211720.png" 
                alt="Мой Куток" 
                className="h-10 md:h-12 w-auto"
              />
            </div>
            
            <div className="flex min-w-0 items-center gap-2 sm:gap-3 md:gap-4">
              {/* User location display */}
              {userLocation && (
                <div className="hidden md:flex items-center gap-2 text-gray-600">
                  <MapPin size={16} className="text-primary" />
                  <span className="text-sm">{userLocation.city}</span>
                </div>
              )}

              {user ? (
                <div className="flex shrink-0 items-center gap-2 sm:gap-3">
                  <Link
                    to="/app"
                    className="inline-flex h-9 items-center justify-center gap-1.5 whitespace-nowrap rounded-xl bg-primary px-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary/90 sm:h-auto sm:gap-2 sm:rounded-full sm:px-5 sm:py-2 sm:text-base md:hover:scale-[1.02]"
                  >
                    <span className="sm:hidden">У аплікацыю</span>
                    <span className="hidden sm:inline">Перайсці ў аплікацыю</span>
                    <ArrowRight size={16} className="shrink-0 sm:h-[18px] sm:w-[18px]" strokeWidth={2.25} />
                  </Link>

                  <Link
                    to="/app"
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-600 transition-colors hover:bg-gray-200 sm:h-10 sm:w-10 md:h-auto md:min-w-0 md:w-auto md:max-w-[220px] md:gap-2 md:px-4 md:py-2"
                    aria-label={user.email ?? 'Профіль'}
                  >
                    <User size={18} className="shrink-0 text-gray-600" />
                    <span className="hidden min-w-0 truncate text-sm text-gray-600 md:inline-block">
                      {user.email}
                    </span>
                  </Link>
                </div>
              ) : (
                <>
                  <button
                    onClick={() => {
                      setAuthMode('login');
                      setIsAuthModalOpen(true);
                    }}
                    className="px-4 py-2 text-gray-700 hover:text-primary transition-colors"
                  >
                    Увайсці
                  </button>
                  <button
                    onClick={() => {
                      setAuthMode('signup');
                      setIsAuthModalOpen(true);
                    }}
                    className="px-6 py-2 bg-primary text-white rounded-full hover:bg-primary/90 transition-all duration-300 hover:scale-105"
                  >
                    Рэгістрацыя
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 md:py-32">
        <div className="container mx-auto px-4 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left side - Text content with safe zone */}
            <div className="space-y-8 animate-fade-in lg:pr-12 xl:pr-16">
              <div className="space-y-6">
                <h1 className="text-4xl md:text-6xl font-bold text-gray-900 leading-tight">
                  Адкрывайце
                  <span className="text-primary block">прыгажосць Беларусі</span>
                </h1>
                <p className="text-xl md:text-2xl text-gray-600 leading-relaxed">
  Даследуйце экасцежкі, месцы для адпачынку і схаваныя прыродныя куткі разам з супольнасцю тых, хто шануе беларускую прыроду.
</p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/app"
                  className="group flex items-center justify-center gap-3 px-8 py-4 bg-primary text-white rounded-2xl hover:bg-primary/90 transition-all duration-300 hover:scale-105 hover:shadow-xl text-lg font-semibold"
                >
                  <span>Пачаць даследаванне</span>
                  <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                </Link>
                
                {!user && (
                  <button
                    onClick={() => {
                      setAuthMode('signup');
                      setIsAuthModalOpen(true);
                    }}
                    className="group flex items-center justify-center gap-3 px-8 py-4 border-2 border-gray-300 text-gray-700 rounded-2xl hover:border-primary hover:text-primary transition-all duration-300 hover:scale-105 text-lg font-semibold"
                  >
                    <span>Стварыць акаўнт</span>
                  </button>
                )}
              </div>
            </div>

            {/* Right side - Featured Location Carousel with safe zone */}
            <div className="relative lg:pl-8 xl:pl-12">
              {featuredLocations.length > 0 && (
                <div className="relative h-[500px] w-full">
                  {featuredLocations.map((location, index) => {
                    const isActive = index === currentLocationIndex;
                    const isPrev = index === (currentLocationIndex - 1 + featuredLocations.length) % featuredLocations.length;
                    const isNext = index === (currentLocationIndex + 1) % featuredLocations.length;
                    
                    let transform = 'translateX(100%) scale(0.8)';
                    let zIndex = 1;
                    let opacity = 0;
                    
                    if (isActive) {
                      transform = 'translateX(0) scale(1) rotate(0deg)';
                      zIndex = 10;
                      opacity = 1;
                    } else if (isPrev) {
                      transform = 'translateX(-20%) scale(0.9) rotate(-3deg)';
                      zIndex = 5;
                      opacity = 0.7;
                    } else if (isNext) {
                      transform = 'translateX(20%) scale(0.9) rotate(3deg)';
                      zIndex = 5;
                      opacity = 0.7;
                    }
                    
                    const distance = locationDistances[location.id];
                    
                    return (
                      <div
                        key={location.id}
                        className="absolute inset-0 transition-all duration-700 ease-in-out cursor-pointer"
                        style={{
                          transform,
                          zIndex,
                          opacity
                        }}
                        onClick={() => handleLocationClick(location)}
                      >
                        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden h-full hover:shadow-3xl transition-shadow duration-300">
                          <div className="relative">
                            <img
                              src={location.images?.[0] || 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&h=400&fit=crop'}
                              alt={location.name}
                              className="w-full h-80 object-cover"
                            />
                            
                            {/* Navigation arrows - only on active card */}
                            {isActive && featuredLocations.length > 1 && (
                              <>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    prevLocation();
                                  }}
                                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 p-2 rounded-full shadow-lg transition-all duration-200 hover:scale-110"
                                >
                                  <ChevronLeft size={20} />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    nextLocation();
                                  }}
                                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 p-2 rounded-full shadow-lg transition-all duration-200 hover:scale-110"
                                >
                                  <ChevronRight size={20} />
                                </button>
                              </>
                            )}
                            
                            {/* Dots indicator - only on active card */}
                            {isActive && featuredLocations.length > 1 && (
                              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                                {featuredLocations.map((_, dotIndex) => (
                                  <button
                                    key={dotIndex}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setCurrentLocationIndex(dotIndex);
                                    }}
                                    className={`w-2 h-2 rounded-full transition-all duration-200 ${
                                      dotIndex === currentLocationIndex 
                                        ? 'bg-white w-6' 
                                        : 'bg-white/60 hover:bg-white/80'
                                    }`}
                                  />
                                ))}
                              </div>
                            )}
                          </div>
                          
                          <div className="p-6">
                            <h3 className="text-xl font-bold text-gray-900 mb-2">{location.name}</h3>
                            <p className="text-gray-600 mb-4 line-clamp-2">{location.shortDescription}</p>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <MapPin size={16} className="text-primary" />
                                <span className="text-sm text-gray-600">
                                  {location.coordinates.lat.toFixed(2)}, {location.coordinates.lng.toFixed(2)}
                                </span>
                              </div>
                              {distance && (
                                <div className="flex items-center gap-4 text-sm">
                                  <div className="flex items-center gap-1">
                                    <Navigation size={14} className="text-primary" />
                                    <span className="font-medium">{distance.distance} км</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <Clock size={14} className="text-primary" />
                                    <span className="font-medium">{formatDuration(distance.duration)}</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center group hover:scale-105 transition-transform duration-300">
              <div className="text-3xl md:text-4xl font-bold text-primary mb-2 group-hover:text-green-600 transition-colors">
                {stats.totalLocations}+
              </div>
              <div className="text-gray-600 font-medium">{getLocationWord(stats.totalLocations)}</div>
            </div>
            <div className="text-center group hover:scale-105 transition-transform duration-300">
              <div className="text-3xl md:text-4xl font-bold text-primary mb-2 group-hover:text-green-600 transition-colors">
                {stats.totalUsers}+
              </div>
              <div className="text-gray-600 font-medium">{getUserWord(stats.totalUsers)}</div>
            </div>
            <div className="text-center group hover:scale-105 transition-transform duration-300">
              <div className="text-3xl md:text-4xl font-bold text-primary mb-2 group-hover:text-green-600 transition-colors">
                {stats.totalPhotos}+
              </div>
              <div className="text-gray-600 font-medium">{getPhotoWord(stats.totalPhotos)}</div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works - Redesigned */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
              Як гэта працуе?
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Проста і зручна - усяго тры крокі да вашага ідэальнага адпачынку
            </p>
          </div>

          <div className="relative">
            {/* Connection lines for desktop */}
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-primary/20 via-primary to-primary/20 transform -translate-y-1/2"></div>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 relative">
              {[
                {
                  step: '01',
                  title: 'Знайдзіце лакацыю',
                  description: 'Выкарыстоўвайце карту або фільтры для пошуку ідэальнага месца',
                  icon: Search,
                  color: 'from-blue-500 to-blue-600'
                },
                {
                  step: '02',
                  title: 'Плануйце паездку',
                  description: 'Глядзіце фотаздымкі, чытайце водгукі і атрымлівайце маршрут',
                  icon: Compass,
                  color: 'from-green-500 to-green-600'
                },
                {
                  step: '03',
                  title: 'Дзяліцеся ўражаннямі',
                  description: 'Дадавайце свае фота і водгукі, каб дапамагчы іншым',
                  icon: Camera,
                  color: 'from-purple-500 to-purple-600'
                }
              ].map((item, index) => {
                const Icon = item.icon;
                
                return (
                  <div key={index} className="relative group">
                    <div className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border border-gray-100">
                      {/* Step number */}
                      <div className="text-6xl font-bold text-primary/10 mb-4 leading-none">
                        {item.step}
                      </div>
                      
                      {/* Icon */}
                      <div className={`w-20 h-20 bg-gradient-to-br ${item.color} rounded-3xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                        <Icon size={36} className="text-white" />
                      </div>
                      
                      <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-primary transition-colors">
                        {item.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed text-lg">
                        {item.description}
                      </p>
                    </div>
                    
                    {/* Connection dots for desktop */}
                    <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-8 bg-primary rounded-full transform -translate-y-1/2 flex items-center justify-center shadow-lg">
                      <div className="w-3 h-3 bg-white rounded-full"></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary to-green-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-5xl font-bold mb-6">
              Гатовы пачаць сваю прыгоду?
            </h2>
            <p className="text-xl md:text-2xl mb-8 opacity-90">
              Далучайцеся да супольнасці аматараў прыроды і адкрывайце 
              найпрыгажэйшыя куткі Беларусі
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/app"
                className="group flex items-center justify-center gap-3 px-8 py-4 bg-white text-primary rounded-2xl hover:bg-gray-100 transition-all duration-300 hover:scale-105 hover:shadow-xl text-lg font-bold"
              >
                <span>Пачаць зараз</span>
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              
              {!user && (
                <button
                  onClick={() => {
                    setAuthMode('signup');
                    setIsAuthModalOpen(true);
                  }}
                  className="flex items-center justify-center gap-3 px-8 py-4 border-2 border-white text-white rounded-2xl hover:bg-white hover:text-primary transition-all duration-300 hover:scale-105 text-lg font-bold"
                >
                  <span>Стварыць акаўнт</span>
                </button>
              )}
            </div>

            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { icon: CheckCircle, text: 'Бясплатна назаўсёды' },
                { icon: CheckCircle, text: 'Без рэкламы' },
                { icon: CheckCircle, text: 'Супольнасць аматараў' }
              ].map((item, index) => {
                const Icon = item.icon;
                return (
                  <div key={index} className="flex items-center justify-center gap-3">
                    <Icon size={24} className="text-white/80" />
                    <span className="text-lg">{item.text}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <img 
                src="https://i.ibb.co/xKYr1D9P/Group-1211720.png" 
                alt="Мой Куток" 
                className="h-12 w-auto mb-4 brightness-0 invert"
              />
              <p className="text-gray-400 leading-relaxed mb-6">
                Мой Куток - гэта платформа для аматараў прыроды, якая дапамагае 
                знаходзіць і дзяліцца цікавымі лакацыямі Беларусі.
              </p>
              <div className="flex items-center gap-4">
                <span className="text-gray-400">Напісаць нам:</span>
                <a 
                  href="mailto:mamanovichks@gmail.com"
                  className="text-primary hover:text-green-400 transition-colors"
                >
                  mamanovichks@gmail.com
                </a>
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-bold mb-4">Навігацыя</h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/app" className="text-gray-400 hover:text-white transition-colors">
                    Аплікацыя
                  </Link>
                </li>
                <li>
                  <Link to="/privacy-policy" className="text-gray-400 hover:text-white transition-colors">
                    Палітыка прыватнасці
                  </Link>
                </li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-bold mb-4">Катэгорыі</h3>
              <ul className="space-y-2 text-gray-400">
                <li>Экасцежкі</li>
                <li>Кемпінг</li>
                <li>Гістарычныя месцы</li>
                <li>Назіранне за зоркамі</li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2026 Мой Куток. Усе правы абаронены.</p>
          </div>
        </div>
      </footer>

      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        initialMode={authMode}
      />
    </div>
  );
};